import { Box, Flex, Text } from '@pancakeswap/uikit'
import Link from 'next/link'
import styled from 'styled-components'
import useSWR from 'swr'
import { usePublicClient } from 'wagmi'
import { DexLogo } from '../pool-search/PoolSearch'
import { V3_CHAIN_ID } from '../config'
import { usePoolPair, useTokenMeta } from '../hooks'
import { poolInfoLink, shortId } from '../poolLinks'
import { whenBoth } from '../rules'
import { Display, Num } from '../ui'
import { CopyButton, ScanLink } from './AddressActions'
import TokenIcon from './TokenIcon'
import { Panel } from './Shell'

const Rows = styled.dl`
  display: grid;
  gap: 14px;
  margin: 0;
`
const Item = styled.div`
  display: grid;
  gap: 4px;
  min-width: 0;
  dt {
    font-size: 12px;
    color: ${({ theme }) => theme.colors.textSubtle};
  }
  dd {
    margin: 0;
    min-width: 0;
    font-size: 14px;
  }
`
const Line = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  max-width: 100%;
  font-family: 'Martian Mono', ui-monospace, monospace;
  font-size: 13px;
`

/** A hash or an address: shortened (0x5760…600c), one button copies the whole value (with a toast), one link opens it on BscScan. */
export function AddressLine({ value, kind = 'address', label }: { value: string; kind?: 'address' | 'tx'; label?: string }) {
  const what = label ?? (kind === 'tx' ? 'transaction hash' : 'address')
  return (
    <Line>
      <span title={value} data-testid="v3-short">{shortId(value)}</span>
      <CopyButton value={value} label={what} />
      <ScanLink value={value} kind={kind} label={label ?? 'it'} />
    </Line>
  )
}

function TokenItem({ address }: { address: string }) {
  const t = useTokenMeta(address, true)
  return (
    <Flex alignItems="center" style={{ gap: 10, minWidth: 0 }} flexWrap="wrap">
      <TokenIcon address={address} symbol={t.symbol} decimals={t.decimals} size={28} />
      <Link href={`/token/${address}`} style={{ fontWeight: 800, textDecoration: 'underline' }} data-testid="v3-token-link">{t.symbol ?? shortId(address)}</Link>
      <AddressLine value={address} label="token" />
    </Flex>
  )
}

/** Block time of the creation transaction, so the card can say when the competition was created. */
function useCreatedAt(tx?: string) {
  const client = usePublicClient({ chainId: V3_CHAIN_ID })
  return useSWR(tx ? `created:${tx}` : null, async () => {
    const r = await client.getTransactionReceipt({ hash: tx as `0x${string}` })
    const b = await client.getBlock({ blockNumber: r.blockNumber })
    return Number(b.timestamp)
  }, { revalidateOnFocus: false, shouldRetryOnError: false, dedupingInterval: 600_000 })
}

export interface DetailsCardProps {
  pool: string
  creator: string
  projectToken: string
  rewardToken: string
  createdTx?: string
  refundAfter: number
}

/** The Details card: every address and hash short, copyable and openable; the pool with its exchange, pair and fee; both tokens with their logo and a link to their token page. */
export default function DetailsCard({ pool, creator, projectToken, rewardToken, createdTx, refundAfter }: DetailsCardProps) {
  const pair = usePoolPair(pool)
  const created = useCreatedAt(createdTx)
  const info = poolInfoLink(pair.dex?.name, pool)
  const refund = whenBoth(refundAfter)
  const made = created.data ? whenBoth(created.data) : null
  return (
    <Panel data-testid="v3-details">
      <Display bold fontSize="18px" mb="12px">Details</Display>
      <Rows>
        <Item>
          <dt>Creator</dt>
          <dd><AddressLine value={creator} label="creator" /></dd>
        </Item>
        <Item>
          <dt>Pool</dt>
          <dd>
            <Flex alignItems="center" style={{ gap: 8, minWidth: 0 }} flexWrap="wrap">
              {pair.dex && <DexLogo dex={pair.dex.name} size={22} />}
              {info.external ? (
                <a href={info.href} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 800, textDecoration: 'underline', overflowWrap: 'anywhere' }} data-testid="v3-pool-link">
                  {pair.dex ? pair.dex.name : 'Pool'}{pair.symbol0 && pair.symbol1 ? ` · ${pair.symbol0}/${pair.symbol1}` : ''}
                </a>
              ) : (
                <Link href={info.href} style={{ fontWeight: 800, textDecoration: 'underline', overflowWrap: 'anywhere' }} data-testid="v3-pool-link">
                  {pair.dex ? pair.dex.name : 'Pool'}{pair.symbol0 && pair.symbol1 ? ` · ${pair.symbol0}/${pair.symbol1}` : ''}
                </Link>
              )}
            </Flex>
            <Box mt="4px"><AddressLine value={pool} label="pool" /></Box>
          </dd>
        </Item>
        <Item>
          <dt>Project token</dt>
          <dd><TokenItem address={projectToken} /></dd>
        </Item>
        <Item>
          <dt>Prize token</dt>
          <dd><TokenItem address={rewardToken} /></dd>
        </Item>
        {createdTx && (
          <Item>
            <dt>Created in</dt>
            <dd>
              <AddressLine value={createdTx} kind="tx" label="creation transaction" />
              {made && <Text fontSize="12px" color="textSubtle" mt="2px">{made.utc} · {made.local}</Text>}
            </dd>
          </Item>
        )}
      </Rows>
      <Text fontSize="12px" color="textSubtle" mt="16px" data-testid="v3-refund-note">
        If no result is published by <Num>{refund.utc}</Num> ({refund.local}), the whole prize can be returned to the creator.
      </Text>
    </Panel>
  )
}
